import React, { createContext, useContext, useState, ReactNode } from 'react';
import { StockViewModal } from '../components/market/StockViewModal';
import { useLocation } from 'react-router-dom'; // Add this import

interface StockViewContextType {
  openStockModal: (ticker: string) => void;
  closeStockModal: () => void;
}

const StockViewContext = createContext<StockViewContextType | undefined>(undefined);

interface StockViewProviderProps {
  children: ReactNode;
  onAnalyze?: (prompt: string) => void;
  fullScreenMode?: boolean;
}

export const StockViewProvider: React.FC<StockViewProviderProps> = ({ 
  children, 
  onAnalyze,
  fullScreenMode = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTicker, setCurrentTicker] = useState<string>('');
  const location = useLocation(); // Get current location to check if we're on the home page
  
  // Check if current location is the home page
  const isHomePage = location.pathname === '/' || location.pathname === '/home';

  const openStockModal = (ticker: string) => {
    setCurrentTicker(ticker);
    setIsModalOpen(true);
  };

  const closeStockModal = () => {
    setIsModalOpen(false);
  };

  return (
    <StockViewContext.Provider value={{ openStockModal, closeStockModal }}>
      {children}
      <StockViewModal 
        isOpen={isModalOpen} 
        onClose={closeStockModal} 
        ticker={currentTicker} 
        onAnalyze={onAnalyze}
        fullScreenMode={fullScreenMode}
        fromHomePage={isHomePage} // Pass the isHomePage flag
      />
    </StockViewContext.Provider>
  );
};

export const useStockView = (): StockViewContextType => {
  const context = useContext(StockViewContext);
  if (context === undefined) {
    throw new Error('useStockView must be used within a StockViewProvider');
  }
  return context;
};
